/**
 * ================================================================
 * AUTH STORE - Slim Zustand State Management
 * ================================================================
 *
 * Client-only state for authentication metadata.
 * Server state (user data, profiles, authentication status) handled by TanStack Query and BetterAuth.
 *
 * @author MeBi Team
 * @version 4.0.0 - Fully Decoupled from Session State
 */

import { create } from "zustand";
import { persist, subscribeWithSelector } from "zustand/middleware";
import { useProfileRoles } from "@/features/auth/hooks/useProfile";

// ============================================
// TYPES
// ============================================

export type UserRole = "Player" | "Organizer";

export interface SavedAccount {
  id: string;
  email: string;
  phone?: string;
  fullName: string;
  nickname?: string;
  organizerName?: string;
  avatarUrl?: string;
  organizerAvatarUrl?: string;
  roles: UserRole[];
  lastLoginAt: string;
}

export interface AuthStore {
  // Persisted (localStorage via zustand persist)
  savedAccounts: SavedAccount[];
  currentRole: UserRole;

  // Session-only
  tenantId: string;
  currentPlayerId: string | null;

  // Actions
  setRole: (role: UserRole) => void;
  setTenantId: (tenantId: string) => void;
  setCurrentPlayerId: (playerId: string | null) => void;

  // Saved Accounts Management
  addSavedAccount: (account: SavedAccount) => void;
  updateSavedAccount: (id: string, updates: Partial<SavedAccount>) => void;
  removeSavedAccount: (id: string) => void;
  clearAllAccounts: () => void;

  // Logout (Only clears metadata, tokens are handled via BetterAuth)
  logout: () => Promise<void>;
}

// ============================================
// INITIAL STATE
// ============================================

const initialState = {
  savedAccounts: [] as SavedAccount[],
  currentRole: "Player" as UserRole,
  tenantId: "main",
  currentPlayerId: null as string | null,
};

const isValidUserRole = (role: unknown): role is UserRole => {
  return role === "Player" || role === "Organizer";
};

// ============================================
// STORE
// ============================================

export const useAuthStore = create<AuthStore>()(
  subscribeWithSelector(
    persist(
      (set) => ({
        ...initialState,

        setRole: (role) => set({ currentRole: role }),

        setTenantId: (tenantId) => set({ tenantId }),

        setCurrentPlayerId: (currentPlayerId) => set({ currentPlayerId }),

        addSavedAccount: (account) =>
          set({
            savedAccounts: [account],
          }),

        updateSavedAccount: (id, updates) =>
          set((state) => ({
            savedAccounts: state.savedAccounts.map((a) =>
              a.id === id ? { ...a, ...updates } : a,
            ),
          })),

        removeSavedAccount: (id) =>
          set((state) => ({
            savedAccounts: state.savedAccounts.filter((a) => a.id !== id),
          })),

        clearAllAccounts: () => set({ savedAccounts: [] }),

        logout: async () => {
          set({
            currentRole: "Player",
            tenantId: "main",
            currentPlayerId: null,
          });
        },
      }),
      {
        name: "mebi-app-state",
        // Only persist currentRole - savedAccounts are managed by Capacitor Preferences
        // This prevents rehydration issues where cleared accounts get restored
        partialize: (state) => ({
          currentRole: state.currentRole,
        }),
        merge: (persistedState, currentState) => {
          const persisted = persistedState as Partial<AuthStore> | undefined;

          return {
            ...currentState,
            currentRole: isValidUserRole(persisted?.currentRole)
              ? persisted.currentRole
              : currentState.currentRole,
            savedAccounts: currentState.savedAccounts,
          };
        },
      },
    ),
  ),
);

// ============================================
// SELECTOR HOOKS
// ============================================

export const useCurrentRole = () => useAuthStore((state) => state.currentRole);
export const useSavedAccountsStore = () =>
  useAuthStore((state) => state.savedAccounts);

// Role-based selectors
export const useIsPlayer = () =>
  useAuthStore((state) => state.currentRole === "Player");
export const useIsOrganizer = () =>
  useAuthStore((state) => state.currentRole === "Organizer");

// ============================================
// FUNCTIONS (for non-hook usage)
// ============================================

export const switchToRole = async (role: UserRole) => {
  useAuthStore.getState().setRole(role);
};

export const getCurrentRole = (): UserRole => {
  return useAuthStore.getState().currentRole;
};

// Helper function to determine role from path
export const getCurrentRoleFromPath = (pathname: string): UserRole => {
  if (
    pathname.startsWith("/organizer") ||
    pathname.includes("/tournaments/create")
  ) {
    return "Organizer";
  }
  return "Player";
};

// ============================================
// LEGACY TYPES & HOOKS (Backward compatibility)
// ============================================

export type SystemRole = "user" | "admin" | "SYSTEM_ADMIN";
export type TenantRole = "member" | "organizer";

export interface UserProfile {
  id: string;
  email: string;
  phone?: string;
  fullName: string;
  nickname?: string;
  avatarUrl?: string;
  city?: string;
  ward?: string;
}

// RoleManager hook for screens that need multiple role-related values
interface RoleManagerResult {
  currentRole: UserRole;
  setCurrentRole: (role: UserRole) => void;
  availableRoles: UserRole[];
  isPlayer: boolean;
  isOrganizer: boolean;
  isRoleSwitching: boolean;
  getCurrentRoleFromPath: (pathname: string) => UserRole;
}

export const useRoleManager = Object.assign(
  (): RoleManagerResult => {
    const store = useAuthStore();
    // Get dynamic roles from profile - NOT hardcoded!
    const profileRoles = useProfileRoles();
    const dynamicRoles =
      profileRoles && Array.isArray(profileRoles) && profileRoles.length > 0
        ? (profileRoles as UserRole[])
        : (["Player"] as UserRole[]);
    return {
      currentRole: store.currentRole,
      setCurrentRole: store.setRole,
      availableRoles: dynamicRoles,
      isPlayer: store.currentRole === "Player",
      isOrganizer: store.currentRole === "Organizer",
      isRoleSwitching: false,
      getCurrentRoleFromPath,
    };
  },
  {
    getState: (): RoleManagerResult => {
      const store = useAuthStore.getState();
      // For getState (non-hook context), fallback to Player only
      // The actual roles should be managed through React hooks
      return {
        currentRole: store.currentRole,
        setCurrentRole: store.setRole,
        availableRoles: ["Player"] as UserRole[],
        isPlayer: store.currentRole === "Player",
        isOrganizer: store.currentRole === "Organizer",
        isRoleSwitching: false,
        getCurrentRoleFromPath,
      };
    },
  },
);

// useAvailableRoles - fetches from TanStack Query profile hook
export const useAvailableRoles = (): UserRole[] => {
  const roles = useProfileRoles();
  if (roles && Array.isArray(roles) && roles.length > 0) {
    return roles as UserRole[];
  }
  return ["Player"];
};

// useUserContext - backward compatibility hook (deprecated, use useSession or useCurrentUser instead)
export const useUserContext = Object.assign(
  () => {
    const store = useAuthStore();
    // Get dynamic roles from profile - NOT hardcoded!
    const profileRoles = useProfileRoles();
    const dynamicRoles =
      profileRoles && Array.isArray(profileRoles) && profileRoles.length > 0
        ? (profileRoles as UserRole[])
        : (["Player"] as UserRole[]);
    return {
      ...store,
      user: null as UserProfile | null, // Removed currentUserId context dependency. Fetch user via useCurrentUser!
      isPlayer: store.currentRole === "Player",
      isOrganizer: store.currentRole === "Organizer",
      systemRole: "user" as SystemRole,
      switchRole: store.setRole,
      availableRoles: dynamicRoles,
    };
  },
  {
    getState: () => {
      const store = useAuthStore.getState();
      return {
        ...store,
        user: null as UserProfile | null, // Removed currentUserId context dependency.
        isPlayer: store.currentRole === "Player",
        isOrganizer: store.currentRole === "Organizer",
        systemRole: "user" as SystemRole,
      };
    },
    subscribe: useAuthStore.subscribe,
  },
);

// useUser - simple user selector (deprecated, use useCurrentUser instead)
export const useUser = () => null;

// useIsLoading - always false (loading handled by TanStack Query)
export const useIsLoading = () => false;
